import { useGoals } from 'features/goals';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from 'shared/components/ui/confirm-dialog';
import { PopupMenu } from 'shared/components/ui/popup-menu';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { ID } from 'shared/types/id';
import { ContextMenuOptions } from 'shared/views/goal-filter-list/types';

import { CompleteList } from './complete-list';
import { ConvertGoalTypeList } from './convert-goal-type-list';
import { EditList } from './edit-list';
import { GoalTreeContextMenu } from './goal-tree-context-menu';
import { ManageList } from './manage-list';
import { MoveGoalList } from './move-goal-list';

export type GoalContextMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'ref'
> & {
  id: ID;
  onClose: () => void;
  excludedOptions?: ContextMenuOptions[];
  /** The link the row this menu was opened from sits under — the one "Move to" replaces. */
  sourceLink?: { goalId?: ID; lifeAreaId?: ID };
};

export const GoalContextMenu: React.FC<GoalContextMenuProps> = ({
  id,
  onClose,
  position,
  location,
  referenceElement,
  excludedOptions,
  sourceLink,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  const { menuOpen: goalSelectOpen, openMenu: openGoalSelect } = useOpenMenu();
  const { menuOpen: convertBlockedOpen, openMenu: openConvertBlocked } =
    useOpenMenu();

  const goals = useGoals();
  const goal = useMemo(() => goals.find((goal) => goal.id === id), [goals, id]);
  const onOutside = () => {
    if (!goalSelectOpen) {
      onClose();
    }
  };

  return goal ? (
    <>
      {!goalSelectOpen ? (
        <PopupMenu
          ref={containerRef}
          location={location}
          position={position}
          referenceElement={referenceElement}
          onOutside={onOutside}
        >
          <EditList goal={goal} onClose={onClose} />
          {/* <MetricList goal={goal} onClose={onClose} /> */}
          <CompleteList goal={goal} onClose={onClose} />
          <MoveGoalList
            goal={goal}
            onClose={onClose}
            openGoalSelect={openGoalSelect}
          />
          <ConvertGoalTypeList
            goal={goal}
            onClose={onClose}
            openConvertBlocked={openConvertBlocked}
          />
          {/* <ViewList
            goal={goal}
            onClose={onClose}
            excludedOptions={excludedOptions}
          /> */}
          <ManageList
            goal={goal}
            onClose={onClose}
            excludedOptions={excludedOptions}
          />
          {convertBlockedOpen && (
            <ConfirmDialog
              open
              abovePopups
              hideConfirm
              title={t('context-menus.goal.convert-blocked.title')}
              message={t('context-menus.goal.convert-blocked.message')}
              cancelLabel={t('general.close')}
              onConfirm={onClose}
              onCancel={onClose}
            />
          )}
        </PopupMenu>
      ) : (
        <GoalTreeContextMenu
          id={id}
          sourceLink={sourceLink}
          location={location}
          position={position}
          referenceElement={referenceElement}
          onClose={onClose}
        />
      )}
    </>
  ) : null;
};
